const PLAY_ICON = `<path d="M14 86.9262V9.07381C14 6.792 16.4464 5.34552 18.4458 6.44516L89.2206 45.3714C91.293 46.5111 91.293 49.4889 89.2206 50.6286L18.4458 89.5548C16.4464 90.6545 14 89.208 14 86.9262Z" fill="currentColor" />`;
const PAUSE_ICON = `<rect x="24" y="18" width="18" height="60" rx="2" fill="currentColor" /><rect x="54" y="18" width="18" height="60" rx="2" fill="currentColor" />`;

const SOUNDCLOUD_SDK_URL = 'https://w.soundcloud.com/player/api.js';
/** @type {Promise<void> | null} */
let soundCloudSdkPromise = null;

/**
 * @typedef {'local' | 'soundcloud'} ABSourceType
 */

/**
 * @typedef {{
 *   Widget: ((iframe: HTMLIFrameElement) => any) & {
 *     Events: {
 *       READY: string;
 *       PLAY: string;
 *       PAUSE: string;
 *       FINISH: string;
 *       PLAY_PROGRESS: string;
 *     };
 *   };
 * }} SoundCloudGlobal
 */

/**
 * @typedef {{
 *   sourceType: 'local';
 *   beforeUrl: string;
 *   afterUrl: string;
 * }} ABLocalTrackConfig
 */

/**
 * @typedef {{
 *   sourceType: 'soundcloud';
 *   beforeId: string;
 *   afterId: string;
 *   beforeSecret?: string;
 *   afterSecret?: string;
 * }} ABSoundCloudTrackConfig
 */

/** @typedef {ABLocalTrackConfig | ABSoundCloudTrackConfig} ABTrackConfig */

/**
 * @typedef {{
 *   sourceType?: ABSourceType;
 *   beforeUrl?: string;
 *   afterUrl?: string;
 *   beforeId?: string;
 *   afterId?: string;
 *   beforeSecret?: string;
 *   afterSecret?: string;
 *   autoplay?: boolean;
 *   resetProgress?: boolean;
 *   source?: 'before' | 'after';
 * }} ABSetTracksConfig
 */

/** @returns {SoundCloudGlobal | null} */
function getSoundCloudGlobal() {
  const win = /** @type {{ SC?: unknown }} */ (window);
  const sc = /** @type {unknown} */ (win.SC);
  if (!sc || typeof sc !== 'object') return null;
  const withWidget = /** @type {{ Widget?: unknown }} */ (sc);
  if (!withWidget.Widget) return null;
  return /** @type {SoundCloudGlobal} */ (sc);
}

/**
 * @returns {Promise<void>}
 */
function loadSoundCloudSdk() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('ABPlayer: SoundCloud SDK cannot load on server'));
  }

  if (getSoundCloudGlobal()?.Widget) {
    return Promise.resolve();
  }

  if (soundCloudSdkPromise) {
    return soundCloudSdkPromise;
  }

  soundCloudSdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${SOUNDCLOUD_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(undefined), { once: true });
      existing.addEventListener('error', () => reject(new Error('ABPlayer: Failed to load SoundCloud SDK')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = SOUNDCLOUD_SDK_URL;
    script.async = true;
    script.onload = () => resolve(undefined);
    script.onerror = () => reject(new Error('ABPlayer: Failed to load SoundCloud SDK'));
    document.head.appendChild(script);
  });

  return soundCloudSdkPromise;
}

/**
 * @typedef {{
 *   ready: boolean;
 *   paused: boolean;
 *   duration: number;
 *   currentTime: number;
 *   volume: number;
 *   play: () => Promise<void>;
 *   pause: () => void;
 *   addEventListener: (eventName: string, handler: () => void) => void;
 *   removeEventListener: (eventName: string, handler: () => void) => void;
 *   destroy?: () => void;
 * }} ABTrack
 */

class LocalAudioTrack {
  /** @param {string} url */
  constructor(url) {
    this.audio = new Audio();
    this.audio.preload = 'none';
    this.audio.src = url;
  }

  get ready() {
    return true;
  }

  get paused() {
    return this.audio.paused;
  }

  get duration() {
    const d = this.audio.duration;
    return Number.isFinite(d) ? d : 0;
  }

  get currentTime() {
    const t = this.audio.currentTime;
    return Number.isFinite(t) ? t : 0;
  }

  set currentTime(value) {
    this.audio.currentTime = Math.max(0, Number(value) || 0);
  }

  get volume() {
    return this.audio.volume;
  }

  set volume(value) {
    this.audio.volume = Math.max(0, Math.min(1, Number(value) || 0));
  }

  play() {
    return this.audio.play();
  }

  pause() {
    this.audio.pause();
  }

  /** @param {string} eventName @param {() => void} handler */
  addEventListener(eventName, handler) {
    this.audio.addEventListener(eventName, handler);
  }

  /** @param {string} eventName @param {() => void} handler */
  removeEventListener(eventName, handler) {
    this.audio.removeEventListener(eventName, handler);
  }

  destroy() {
    this.audio.pause();
    this.audio.src = '';
  }
}

class SoundCloudTrack {
  /**
   * @param {{ trackId: string; secretToken?: string; host: HTMLElement }} config
   */
  constructor({ trackId, secretToken = '', host }) {
    this.trackId = trackId;
    this.secretToken = secretToken;
    this.host = host;
    /** @type {any} */
    this.widget = null;
    this.ready = false;
    this.paused = true;
    this._duration = 0;
    this._currentTime = 0;
    this._volume = 1;
    this.destroyed = false;
    /** @type {{ eventName: string; handler: (...args: any[]) => void }[]} */
    this.widgetBindings = [];
    /** @type {Map<string, Set<() => void>>} */
    this.listeners = new Map();

    this.iframe = document.createElement('iframe');
    this.iframe.setAttribute('allow', 'autoplay');
    this.iframe.setAttribute('tabindex', '-1');
    this.iframe.setAttribute('aria-hidden', 'true');
    this.iframe.className = 'abp__soundcloud-frame';
    this.iframe.src = this.buildEmbedUrl();
    this.host.appendChild(this.iframe);

    this.init();
  }

  buildEmbedUrl() {
    const secret = this.secretToken ? `?secret_token=${encodeURIComponent(this.secretToken)}` : '';
    const trackUrl = `https://api.soundcloud.com/tracks/${encodeURIComponent(this.trackId)}${secret}`;
    const params = new URLSearchParams({
      url: trackUrl,
      auto_play: 'false',
      buying: 'false',
      sharing: 'false',
      download: 'false',
      show_comments: 'false',
      show_user: 'false',
      show_reposts: 'false',
      show_teaser: 'false',
      hide_related: 'true',
      visual: 'false',
    });
    return `https://w.soundcloud.com/player/?${params.toString()}`;
  }

  /** @param {string} eventName */
  emit(eventName) {
    const handlers = this.listeners.get(eventName);
    if (!handlers) return;
    handlers.forEach((handler) => {
      try {
        handler();
      } catch (_) {}
    });
  }

  /** @param {string} eventName @param {(...args: any[]) => void} handler */
  bindWidgetEvent(eventName, handler) {
    if (!this.widget) return;
    this.widget.bind(eventName, handler);
    this.widgetBindings.push({ eventName, handler });
  }

  async init() {
    try {
      await loadSoundCloudSdk();
      const sc = getSoundCloudGlobal();
      if (this.destroyed || !sc?.Widget) return;

      this.widget = sc.Widget(this.iframe);

      this.bindWidgetEvent(sc.Widget.Events.READY, () => {
        this.ready = true;
        if (!this.widget) return;

        this.widget.setVolume(Math.round(this.volume * 100));
        this.widget.getDuration((/** @type {number} */ ms) => {
          this.duration = Math.max(0, (Number(ms) || 0) / 1000);
        });
        if (this.currentTime > 0) {
          this.widget.seekTo(Math.round(this.currentTime * 1000));
        }
        this.emit('ready');
      });

      this.bindWidgetEvent(sc.Widget.Events.PLAY, () => {
        this.paused = false;
        this.emit('play');
      });

      this.bindWidgetEvent(sc.Widget.Events.PAUSE, () => {
        this.paused = true;
        this.emit('pause');
      });

      this.bindWidgetEvent(sc.Widget.Events.FINISH, () => {
        this.paused = true;
        this._currentTime = 0;
        this.emit('ended');
      });

      this.bindWidgetEvent(sc.Widget.Events.PLAY_PROGRESS, (/** @type {{ currentPosition?: number; duration?: number } | undefined} */ data) => {
        const position = Number(data?.currentPosition) || 0;
        const duration = Number(data?.duration) || 0;
        this._currentTime = Math.max(0, position / 1000);
        if (duration > 0) {
          this._duration = duration / 1000;
        }
      });
    } catch (_) {
      this.emit('error');
    }
  }

  play() {
    if (this.destroyed || !this.ready || !this.widget) {
      return Promise.reject(new Error('ABPlayer: SoundCloud track is not ready'));
    }
    this.widget.play();
    return Promise.resolve();
  }

  pause() {
    if (this.destroyed || !this.ready || !this.widget) return;
    this.widget.pause();
    this.paused = true;
  }

  set currentTime(value) {
    this._currentTime = Math.max(0, Number(value) || 0);
    if (this.ready && this.widget) {
      this.widget.seekTo(Math.round(this._currentTime * 1000));
    }
  }

  get currentTime() {
    return this._currentTime || 0;
  }

  set duration(value) {
    this._duration = Math.max(0, Number(value) || 0);
  }

  get duration() {
    return this._duration || 0;
  }

  set volume(value) {
    this._volume = Math.max(0, Math.min(1, Number(value) || 0));
    if (this.ready && this.widget) {
      this.widget.setVolume(Math.round(this._volume * 100));
    }
  }

  get volume() {
    return this._volume ?? 1;
  }

  /** @param {string} eventName @param {() => void} handler */
  addEventListener(eventName, handler) {
    if (!this.listeners.has(eventName)) {
      this.listeners.set(eventName, new Set());
    }
    this.listeners.get(eventName)?.add(handler);
  }

  /** @param {string} eventName @param {() => void} handler */
  removeEventListener(eventName, handler) {
    this.listeners.get(eventName)?.delete(handler);
  }

  destroy() {
    this.destroyed = true;
    if (this.widget && getSoundCloudGlobal()?.Widget?.Events) {
      const unboundEvents = new Set(this.widgetBindings.map((item) => item.eventName));
      unboundEvents.forEach((eventName) => {
        try {
          this.widget?.unbind(eventName);
        } catch (_) {}
      });
    }
    this.widgetBindings = [];
    this.listeners.clear();
    if (this.iframe?.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
    }
  }
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

class ABPlayerElement extends HTMLElement {
  static observedAttributes = [
    'data-source-type',
    'data-before-url',
    'data-after-url',
    'data-before-id',
    'data-after-id',
    'data-before-secret',
    'data-after-secret',
    'data-color',
    'data-size'
  ];

  /** @type {{ source: 'before' | 'after'; playing: boolean; progress: number }} */
  #state = {
    source: 'before',
    playing: false,
    progress: 0,
  };

  /** @type {ABTrack | null} */
  #audioBefore = null;
  /** @type {ABTrack | null} */
  #audioAfter = null;
  /** @type {number | null} */
  #rafId = null;
  /** @type {{
   *   ringContainer: HTMLDivElement | null;
   *   playBtn: HTMLButtonElement | null;
   *   iconPlay: SVGGElement | null;
   *   iconPause: SVGGElement | null;
   *   progress: SVGCircleElement | null;
   *   progressDotContainer: HTMLDivElement | null;
   *   toggleIndicator: HTMLDivElement | null;
  *   soundcloudHost: HTMLDivElement | null;
   *   sources: HTMLButtonElement[];
   * }}
   */
  #els = {
    ringContainer: null,
    playBtn: null,
    iconPlay: null,
    iconPause: null,
    progress: null,
    progressDotContainer: null,
    toggleIndicator: null,
    soundcloudHost: null,
    sources: [],
  };
  #initialized = false;
  #isUpdatingTracks = false;
  #hasTrackError = false;
  #playRequested = false;

  #onAudioPlay = () => {
    if (!this.#state.playing && !this.#playRequested) {
      // Guard against providers (e.g. SoundCloud widgets) that can emit play on init.
      this.#pausePlayback(false);
      return;
    }

    if (!this.#state.playing) {
      this.#state.playing = true;
      this.#startTick();
      this.#render();
    }

    this.#playRequested = false;
  };

  #onAudioPause = () => {
    const beforePlaying = Boolean(this.#audioBefore && !this.#audioBefore.paused);
    const afterPlaying = Boolean(this.#audioAfter && !this.#audioAfter.paused);
    if (!beforePlaying && !afterPlaying && this.#state.playing) {
      this.#state.playing = false;
      this.#stopTick();
      this.#render();
    }
  };

  #onTrackReady = () => {
    this.#render();
  };

  #onTrackError = () => {
    this.#hasTrackError = true;
    this.#pausePlayback(false);
    this.#render();
  };

  /**
  * @param {ABTrack | null} audio
   * @param {number} time
   */
  #setTimeSafe(audio, time) {
    if (!audio || !Number.isFinite(time)) return;
    try {
      audio.currentTime = Math.max(0, time);
    } catch (_) {}
  }

  #applyVolumes() {
    if (this.#audioBefore) this.#audioBefore.volume = this.#state.source === 'before' ? 1 : 0;
    if (this.#audioAfter) this.#audioAfter.volume = this.#state.source === 'after' ? 1 : 0;
  }

  #usesDualTrackPlayback() {
    return this.#getSourceType() === 'local';
  }

  /**
   * @param {ABTrack} audio
   */
  #bindAudioEvents(audio) {
    audio.addEventListener('play', this.#onAudioPlay);
    audio.addEventListener('pause', this.#onAudioPause);
    audio.addEventListener('ended', this.#onEnded);
    audio.addEventListener('ready', this.#onTrackReady);
    audio.addEventListener('error', this.#onTrackError);
  }

  /**
   * @param {ABTrack} audio
   */
  #unbindAudioEvents(audio) {
    audio.removeEventListener('play', this.#onAudioPlay);
    audio.removeEventListener('pause', this.#onAudioPause);
    audio.removeEventListener('ended', this.#onEnded);
    audio.removeEventListener('ready', this.#onTrackReady);
    audio.removeEventListener('error', this.#onTrackError);
  }

  /**
   * @param {ABTrack | null} audio
   * @returns {null}
   */
  #releaseAudio(audio) {
    if (!audio) return null;
    this.#unbindAudioEvents(audio);
    audio.pause();
    if (typeof audio.destroy === 'function') {
      audio.destroy();
    }
    return null;
  }

  /**
   * @param {string} url
   * @returns {ABTrack | null}
   */
  #createAudio(url) {
    if (!url) return null;
    const audio = new LocalAudioTrack(url);
    this.#bindAudioEvents(audio);
    return audio;
  }

  /**
   * @param {string} trackId
   * @param {string} secretToken
   * @returns {ABTrack | null}
   */
  #createSoundCloudTrack(trackId, secretToken = '') {
    if (!trackId) return null;
    const host = this.#els.soundcloudHost || this;
    const audio = new SoundCloudTrack({ trackId, secretToken, host });
    this.#bindAudioEvents(audio);
    return audio;
  }

  /**
   * @param {{
   *   sourceType: 'local' | 'soundcloud';
   *   beforeUrl?: string;
   *   afterUrl?: string;
   *   beforeId?: string;
   *   afterId?: string;
   *   beforeSecret?: string;
   *   afterSecret?: string;
   * }} config
   */
  #replaceTracks(config) {
    this.#audioBefore = this.#releaseAudio(this.#audioBefore);
    this.#audioAfter = this.#releaseAudio(this.#audioAfter);

    if (config.sourceType === 'soundcloud') {
      this.#audioBefore = this.#createSoundCloudTrack(config.beforeId || '', config.beforeSecret || '');
      this.#audioAfter = this.#createSoundCloudTrack(config.afterId || '', config.afterSecret || '');
      return;
    }

    this.#audioBefore = this.#createAudio(config.beforeUrl || '');
    this.#audioAfter = this.#createAudio(config.afterUrl || '');
  }

  #hasTracks() {
    if (this.#hasTrackError) return false;
    return Boolean(this.#audioBefore && this.#audioAfter && this.#audioBefore.ready && this.#audioAfter.ready);
  }

  /** @returns {ABSourceType} */
  #getSourceType() {
    return this.dataset.sourceType === 'soundcloud' ? 'soundcloud' : 'local';
  }

  /** @returns {ABTrackConfig} */
  #getDatasetTrackConfig() {
    const sourceType = this.#getSourceType();
    if (sourceType === 'soundcloud') {
      return {
        sourceType,
        beforeId: this.dataset.beforeId || '',
        afterId: this.dataset.afterId || '',
        beforeSecret: this.dataset.beforeSecret || '',
        afterSecret: this.dataset.afterSecret || '',
      };
    }

    return {
      sourceType,
      beforeUrl: this.dataset.beforeUrl || '',
      afterUrl: this.dataset.afterUrl || '',
    };
  }

  /** @param {ABTrackConfig} config */
  #hasTrackConfig(config) {
    if (config.sourceType === 'soundcloud') {
      return Boolean(config.beforeId && config.afterId);
    }
    return Boolean(config.beforeUrl && config.afterUrl);
  }

  #pausePlayback(emitEvent = true) {
    this.#playRequested = false;
    if (this.#audioBefore) this.#audioBefore.pause();
    if (this.#audioAfter) this.#audioAfter.pause();
    this.#state.playing = false;
    this.#stopTick();
    this.#render();
    if (emitEvent) {
      this.dispatchEvent(new CustomEvent('abplayer:pause', { bubbles: true }));
    }
  }

  /**
   * @param {number} time
   * @returns {Promise<boolean>}
   */
  async #playBothSynced(time) {
    this.#playRequested = true;
    this.#setTimeSafe(this.#audioBefore, time);
    this.#setTimeSafe(this.#audioAfter, time);
    this.#applyVolumes();
    const active = this.#active;
    const inactive = this.#inactive;
    if (!active) return false;

    // Keep dual playback only for local files. SoundCloud widgets are controlled as a single active stream.
    if (!this.#usesDualTrackPlayback() && inactive) {
      inactive.pause();
    }

    const activeResult = await active.play().then(() => true).catch(() => false);
    if (this.#usesDualTrackPlayback() && inactive) {
      inactive.play().catch(() => {});
    }
    if (!activeResult) {
      this.#playRequested = false;
    }
    return activeResult;
  }

  #onPlayClick = () => {
    if (this.#state.playing) {
      this.pause();
    } else {
      this.play();
    }
  };

  /** @param {MouseEvent} e */
  #onSourceClick = (e) => {
    const btn = /** @type {HTMLButtonElement | null} */ (e.currentTarget);
    const nextSource = btn?.dataset?.source;
    if ((nextSource !== 'before' && nextSource !== 'after') || nextSource === this.#state.source) return;

    const currentTime = this.#active?.currentTime || this.#inactive?.currentTime || 0;
    this.#state.source = nextSource;

    this.#setTimeSafe(this.#audioBefore, currentTime);
    this.#setTimeSafe(this.#audioAfter, currentTime);
    this.#applyVolumes();

    if (this.#state.playing) {
      this.#playBothSynced(currentTime).then((ok) => {
        if (!ok) {
          this.#state.playing = false;
          this.#stopTick();
          this.#render();
        }
      });
    }

    this.dispatchEvent(new CustomEvent('abplayer:sourcechange', {
      bubbles: true,
      detail: { source: nextSource }
    }));

    this.#render();
  };

  /** @param {MouseEvent} e */
  #onRingClick = (e) => {
    const target = /** @type {Element | null} */ (e.target instanceof Element ? e.target : null);
    if (target?.closest('.abp__play-btn')) return;
    const ringContainer = this.#els.ringContainer;
    if (!ringContainer) return;

    const rect = ringContainer.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const dx = e.clientX - centerX;
    const dy = e.clientY - centerY;
    const angle = (Math.atan2(dy, dx) * 180 / Math.PI + 90 + 360) % 360;
    const progress = angle / 360;

    this.seek(progress);
  };

  #onEnded = () => {
    this.#playRequested = false;
    if (this.#audioBefore) this.#audioBefore.pause();
    if (this.#audioAfter) this.#audioAfter.pause();
    this.#setTimeSafe(this.#audioBefore, 0);
    this.#setTimeSafe(this.#audioAfter, 0);
    this.#state.playing = false;
    this.#state.progress = 0;
    this.#stopTick();
    this.#updateProgress();
    this.#render();
    this.dispatchEvent(new CustomEvent('abplayer:ended', { bubbles: true }));
  };

  #tick = () => {
    if (!this.#state.playing) return;

    const active = this.#active;
    const duration = this.#getDuration();

    if (duration > 0 && active) {
      this.#state.progress = Math.max(0, Math.min(1, active.currentTime / duration));
      this.#updateProgress();

      // Local tracks can be kept tightly in sync. SoundCloud runs single-active-track mode.
      const before = this.#audioBefore;
      const after = this.#audioAfter;
      const inactive = this.#inactive;
      if (before && after && inactive && this.#usesDualTrackPlayback()) {
        const drift = Math.abs(before.currentTime - after.currentTime);
          const driftThreshold = 0.08;
          if (drift > driftThreshold) {
          inactive.currentTime = active.currentTime;
        }
      }
    }

    this.#rafId = requestAnimationFrame(this.#tick);
  };

  #startTick() {
    if (!this.#rafId) {
      this.#rafId = requestAnimationFrame(this.#tick);
    }
  }

  #stopTick() {
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }


  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#destroy();
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (name === 'data-color') {
      this.style.setProperty('--abp-color', newValue || '#0085dd');
    }
    if (name === 'data-size') {
      this.style.setProperty('--abp-size', `${newValue || 175}px`);
    }
    if (
      this.#initialized &&
      !this.#isUpdatingTracks &&
      (
        name === 'data-source-type' ||
        name === 'data-before-url' ||
        name === 'data-after-url' ||
        name === 'data-before-id' ||
        name === 'data-after-id' ||
        name === 'data-before-secret' ||
        name === 'data-after-secret'
      )
    ) {
      const trackConfig = this.#getDatasetTrackConfig();
      if (this.#hasTrackConfig(trackConfig)) {
        this.setTracks({ ...trackConfig, resetProgress: true });
      } else {
        this.#replaceTracks({ sourceType: this.#getSourceType() });
        this.reset();
      }
    }
  }

  get #active() {
    return this.#state.source === 'before' ? this.#audioBefore : this.#audioAfter;
  }

  get #inactive() {
    return this.#state.source === 'before' ? this.#audioAfter : this.#audioBefore;
  }

  /** @returns {number} */
  #getDuration() {
    const d = this.#active?.duration ?? 0;
    return Number.isFinite(d) ? d : 0;
  }

  #init() {
    if (this.#initialized) return;

    const sourceType = this.#getSourceType();
    const beforeUrl = this.dataset.beforeUrl || '';
    const afterUrl = this.dataset.afterUrl || '';
    const beforeId = this.dataset.beforeId || '';
    const afterId = this.dataset.afterId || '';
    const beforeSecret = this.dataset.beforeSecret || '';
    const afterSecret = this.dataset.afterSecret || '';
    const color = this.dataset.color || '#0085dd';
    const size = Number.parseInt(this.dataset.size || '', 10) || 175;
    const labelBefore = this.dataset.labelBefore || 'Before';
    const labelAfter = this.dataset.labelAfter || 'After';
    const labelPlay = this.dataset.labelPlay || 'Play';

    this.#initialized = true;

    this.style.setProperty('--abp-color', color);
    this.style.setProperty('--abp-size', `${size}px`);

    const radius = 85;
    const circumference = 2 * Math.PI * radius;
    const center = 87.5;

    this.innerHTML = `
      <div class="abp">
        <div class="abp__ring-container">
          <div class="abp__soundcloud-host" aria-hidden="true"></div>
          <svg class="abp__ring" viewBox="0 0 175 175" aria-hidden="true">
            <circle class="abp__track abp__track--135" cx="${center}" cy="${center}" r="135" />
            <circle class="abp__track abp__track--125" cx="${center}" cy="${center}" r="125" />
            <circle class="abp__track abp__track--113" cx="${center}" cy="${center}" r="113" />
            <circle class="abp__track abp__track--100" cx="${center}" cy="${center}" r="100" />
            <circle class="abp__track abp__track--base" cx="${center}" cy="${center}" r="${radius}" />
            <circle class="abp__progress" cx="${center}" cy="${center}" r="${radius}" 
                    style="stroke-dasharray:${circumference};stroke-dashoffset:${circumference}" />
          </svg>
          <div class="abp__progress-dot-container">
            <div class="abp__progress-dot"></div>
          </div>
          <button class="abp__play-btn" type="button" aria-label="${escapeHtml(labelPlay)}">
            <svg viewBox="0 0 96 96" fill="none" width="80" height="88">
              <g class="abp__icon-play">${PLAY_ICON}</g>
              <g class="abp__icon-pause" hidden>${PAUSE_ICON}</g>
            </svg>
          </button>
        </div>
        <div class="abp__toggle-container">
          <div class="abp__toggle-indicator"></div>
          <div class="abp__toggle" role="group" aria-label="Source">
            <button type="button" class="abp__source is-active" data-source="before">${escapeHtml(labelBefore)}</button>
            <button type="button" class="abp__source" data-source="after">${escapeHtml(labelAfter)}</button>
          </div>
        </div>
      </div>
    `;

    this.#els = {
      ringContainer: this.querySelector('.abp__ring-container'),
      playBtn: this.querySelector('.abp__play-btn'),
      iconPlay: this.querySelector('.abp__icon-play'),
      iconPause: this.querySelector('.abp__icon-pause'),
      progress: this.querySelector('.abp__progress'),
      progressDotContainer: this.querySelector('.abp__progress-dot-container'),
      toggleIndicator: this.querySelector('.abp__toggle-indicator'),
      soundcloudHost: this.querySelector('.abp__soundcloud-host'),
      sources: Array.from(this.querySelectorAll('.abp__source')),
    };

    this.#bindUiEvents();
    if (sourceType === 'soundcloud' && beforeId && afterId) {
      this.setTracks({
        sourceType,
        beforeId,
        afterId,
        beforeSecret,
        afterSecret,
        resetProgress: true,
        source: this.#state.source,
      });
      return;
    }

    if (sourceType === 'local' && beforeUrl && afterUrl) {
      this.setTracks({ sourceType, beforeUrl, afterUrl, resetProgress: true, source: this.#state.source });
      return;
    }

    this.#render();
  }

  #bindUiEvents() {
    const { playBtn, ringContainer, sources } = this.#els;

    if (playBtn) {
      playBtn.addEventListener('click', this.#onPlayClick);
    }
    if (ringContainer) {
      ringContainer.addEventListener('click', this.#onRingClick);
    }

    sources.forEach((btn) => { btn.addEventListener('click', this.#onSourceClick); });
  }

  #unbindUiEvents() {
    const { playBtn, ringContainer, sources } = this.#els;

    if (playBtn) {
      playBtn.removeEventListener('click', this.#onPlayClick);
    }
    if (ringContainer) {
      ringContainer.removeEventListener('click', this.#onRingClick);
    }

    sources.forEach((btn) => { btn.removeEventListener('click', this.#onSourceClick); });
  }

  #destroy() {
    this.#stopTick();
    this.#unbindUiEvents();

    this.#audioBefore = this.#releaseAudio(this.#audioBefore);
    this.#audioAfter = this.#releaseAudio(this.#audioAfter);
    this.#els = {
      ringContainer: null,
      playBtn: null,
      iconPlay: null,
      iconPause: null,
      progress: null,
      progressDotContainer: null,
      toggleIndicator: null,
      soundcloudHost: null,
      sources: [],
    };
    this.#initialized = false;
    this.#hasTrackError = false;
    this.#playRequested = false;
  }

  #updateProgress() {
    if (!this.#els.progress) return;
    const radius = 85;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - this.#state.progress);
    this.#els.progress.style.strokeDashoffset = String(offset);

    if (this.#els.progressDotContainer) {
      const rotation = this.#state.progress * 360;
      this.#els.progressDotContainer.style.transform = `rotate(${rotation}deg)`;
    }
  }

  #render() {
    const { playBtn, iconPlay, iconPause, toggleIndicator, sources } = this.#els;
    const hasTracks = this.#hasTracks();
    const abp = this.querySelector('.abp');

    if (abp) {
      abp.classList.toggle('is-disabled', !hasTracks);
      abp.classList.toggle('is-error', this.#hasTrackError);
    }

    if (playBtn) {
      const label = this.#state.playing
        ? (this.dataset.labelPause || 'Pause')
        : (this.dataset.labelPlay || 'Play');
      playBtn.setAttribute('aria-label', label);
      playBtn.disabled = !hasTracks;
    }

    if (iconPlay) iconPlay.style.display = this.#state.playing ? 'none' : '';
    if (iconPause) iconPause.style.display = this.#state.playing ? '' : 'none';

    const activeIndex = this.#state.source === 'before' ? 0 : 1;
    sources.forEach((btn, index) => {
      const isActive = index === activeIndex;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
      btn.disabled = !hasTracks;
    });

    if (toggleIndicator) {
      const isBefore = this.#state.source === 'before';
      if (isBefore) {
        toggleIndicator.style.transform = 'translateX(0px)';
      } else {
        const container = this.querySelector('.abp__toggle-container');
        const containerWidth = container?.clientWidth || 0;
        const indicatorWidth = toggleIndicator.clientWidth || 0;
        const travel = Math.max(0, containerWidth - indicatorWidth - 10);
        toggleIndicator.style.transform = `translateX(${travel}px)`;
      }
    }
  }

  play() {
    if (!this.#hasTracks()) return;
    this.#playRequested = true;
    const duration = this.#getDuration();
    let currentTime = this.#active?.currentTime || this.#inactive?.currentTime || 0;
    if (duration > 0 && currentTime >= duration - 0.05) {
      currentTime = 0;
      this.#setTimeSafe(this.#audioBefore, 0);
      this.#setTimeSafe(this.#audioAfter, 0);
      this.#state.progress = 0;
      this.#updateProgress();
    }
    const active = this.#active;
    const inactive = this.#inactive;
    if (!active) return;

    this.#setTimeSafe(this.#audioBefore, currentTime);
    this.#setTimeSafe(this.#audioAfter, currentTime);
    this.#applyVolumes();

    this.#state.playing = true;
    this.#startTick();
    this.#render();
    this.dispatchEvent(new CustomEvent('abplayer:play', { bubbles: true }));

    active.play()
      .catch(() => {
        this.#playRequested = false;
        this.#state.playing = false;
        this.#stopTick();
        this.#render();
      });

    if (this.#usesDualTrackPlayback() && inactive) {
      inactive.play().catch(() => {});
    } else if (inactive) {
      inactive.pause();
    }
  }

  pause() {
    this.#pausePlayback(true);
  }

  /** @param {number} progress */
  seek(progress) {
    const duration = this.#getDuration();
    if (!duration) return;
    const t = duration * Math.max(0, Math.min(1, progress));
    this.#setTimeSafe(this.#audioBefore, t);
    this.#setTimeSafe(this.#audioAfter, t);
    this.#state.progress = progress;
    this.#updateProgress();
    this.dispatchEvent(new CustomEvent('abplayer:seek', {
      bubbles: true,
      detail: { progress, time: t }
    }));
  }

  /** @param {'before' | 'after'} source */
  setSource(source) {
    if (source === this.#state.source) return;
    const btn = this.#els.sources.find(b => b.dataset.source === source);
    if (btn) btn.click();
  }

  /**
   * @param {ABSetTracksConfig} config
   */
  setTracks({
    sourceType = 'local',
    beforeUrl,
    afterUrl,
    beforeId,
    afterId,
    beforeSecret,
    afterSecret,
    autoplay = false,
    resetProgress = true,
    source,
  } = /** @type {ABSetTracksConfig} */ ({})) {
    const nextBeforeUrl = beforeUrl || '';
    const nextAfterUrl = afterUrl || '';
    const nextBeforeId = beforeId || '';
    const nextAfterId = afterId || '';
    const nextBeforeSecret = beforeSecret || '';
    const nextAfterSecret = afterSecret || '';
    const nextSourceType = sourceType === 'soundcloud' ? 'soundcloud' : 'local';
    const nextSource = source === 'before' || source === 'after' ? source : this.#state.source;
    const nextTime = resetProgress
      ? 0
      : (this.#active?.currentTime || this.#inactive?.currentTime || 0);

    this.#pausePlayback(false);
    this.#hasTrackError = false;
    this.#state.source = nextSource;
    this.#state.progress = 0;

    this.#isUpdatingTracks = true;
    this.dataset.sourceType = nextSourceType;
    this.dataset.beforeUrl = nextBeforeUrl;
    this.dataset.afterUrl = nextAfterUrl;
    this.dataset.beforeId = nextBeforeId;
    this.dataset.afterId = nextAfterId;
    this.dataset.beforeSecret = nextBeforeSecret;
    this.dataset.afterSecret = nextAfterSecret;
    this.#isUpdatingTracks = false;

    this.#replaceTracks({
      sourceType: nextSourceType,
      beforeUrl: nextBeforeUrl,
      afterUrl: nextAfterUrl,
      beforeId: nextBeforeId,
      afterId: nextAfterId,
      beforeSecret: nextBeforeSecret,
      afterSecret: nextAfterSecret,
    });

    if (this.#audioBefore && this.#audioAfter) {
      this.#setTimeSafe(this.#audioBefore, nextTime);
      this.#setTimeSafe(this.#audioAfter, nextTime);
      this.#applyVolumes();

      const duration = this.#getDuration();
      if (duration > 0) {
        this.#state.progress = Math.max(0, Math.min(1, nextTime / duration));
      }
    }

    this.#updateProgress();
    this.#render();
    this.dispatchEvent(new CustomEvent('abplayer:trackschange', {
      bubbles: true,
      detail: {
        beforeUrl: nextBeforeUrl,
        afterUrl: nextAfterUrl,
        beforeId: nextBeforeId,
        afterId: nextAfterId,
        sourceType: nextSourceType,
        source: this.#state.source,
      }
    }));

    if (autoplay) {
      console.warn('ABPlayer: Autoplay is disabled; user interaction is required to start playback.');
    }
  }

  reset() {
    this.#playRequested = false;
    this.#pausePlayback(false);
    this.#setTimeSafe(this.#audioBefore, 0);
    this.#setTimeSafe(this.#audioAfter, 0);
    this.#state.progress = 0;
    this.#updateProgress();
    this.#render();
  }
}

customElements.define('ab-player', ABPlayerElement);

export { ABPlayerElement };
